import React, { useRef, useState } from "react";
import { DatePicker } from "antd";
import html2canvas from "html2canvas";
import dayjs from "dayjs";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { ApiHook } from "../../hooks/apiHook";
import CurrencyConverter from "../../Currency/CurrencyConverter";
import "../../components/Ewallet/MonthlyIncomeStatement.css";

const SUB_TYPE_PARENT = {
  "upgrade_leg": "leg",
  "repurchase_level_commission": "level_commission",
  "upgrade_level_commission": "level_commission",
  "xup_repurchase_level_commission": "level_commission",
  "xup_upgrade_level_commission": "level_commission",
  "matching_bonus_purchase": "matching_bonus",
  "matching_bonus_upgrade": "matching_bonus",
  "bda_bonus": "bda_incentives",
  "bda_sponsor_bonus": "bda_incentives",
};

const ALLOWED_TYPES = {
  leg:                  "Team Performance Incentive",
  pool_bonus:           "Premium Club Incentive",
  rbv_bonus:            "Repurchase Incentive",
  leadership_incentive: "Leadership Incentive",
  rank_bonus:           "Leadership Recognition Rewards",
  bda_incentives:       "BDA Incentives",
};

const INCOME_ICONS = {
  leg:                  "fas fa-users",
  pool_bonus:           "fas fa-gem",
  rbv_bonus:            "fas fa-arrows-rotate",
  leadership_incentive: "fas fa-flag",
  rank_bonus:           "fas fa-trophy",
  bda_incentives:       "fas fa-sitemap",
};

/* -------------------- RBV bonus split (from legAmount `data` JSON) --------------------
   The single "Repurchase Incentive" row is displayed as two separate rows, sourced from
   the `match_details` object inside each rbv_bonus row's `data` JSON payload. */
const RBV_SPLITS = [
  {
    key:    "rbv_personal",
    label:  "Personal Repurchase Incentive",
    icon:   "fas fa-user",
    fields: {
      gross: "personal_incentive_amount",
      admin: "personal_incentive_service_charge",
      tds:   "personal_incentive_tds",
      net:   "personal_incentive_amount_payable",
    },
  },
  {
    key:    "rbv_team",
    label:  "Team Repurchase Incentive",
    icon:   "fas fa-users",
    fields: {
      gross: "team_incentive_amount",
      admin: "team_incentive_service_charge",
      tds:   "team_incentive_tds",
      net:   "team_incentive_amount_payable",
    },
  },
];

const parseMatchDetails = (data) => {
  if (!data) return null;
  let parsed = data;
  if (typeof data === "string") {
    try { parsed = JSON.parse(data); } catch { return null; }
  }
  return parsed?.match_details ?? null;
};

/* -------------------- Indian-format amount in words -------------------- */
const ONES = ["", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight",
  "Nine", "Ten", "Eleven", "Twelve", "Thirteen", "Fourteen", "Fifteen", "Sixteen",
  "Seventeen", "Eighteen", "Nineteen"];
const TENS = ["", "", "Twenty", "Thirty", "Forty", "Fifty", "Sixty", "Seventy", "Eighty", "Ninety"];

const twoDigits = (n) => (n < 20 ? ONES[n] : TENS[Math.floor(n / 10)] + (n % 10 ? " " + ONES[n % 10] : ""));

const threeDigits = (n) => {
  const h = Math.floor(n / 100);
  const rest = n % 100;
  return [h ? ONES[h] + " Hundred" : "", rest ? twoDigits(rest) : ""].filter(Boolean).join(" ");
};

const numberToIndianWords = (value) => {
  let num = Math.floor(Math.abs(Number(value) || 0));
  if (num === 0) return "Zero";
  const crore = Math.floor(num / 10000000); num %= 10000000;
  const lakh = Math.floor(num / 100000); num %= 100000;
  const thousand = Math.floor(num / 1000); num %= 1000;
  return [
    crore ? threeDigits(crore) + " Crore" : "",
    lakh ? twoDigits(lakh) + " Lakh" : "",
    thousand ? twoDigits(thousand) + " Thousand" : "",
    num ? threeDigits(num) : "",
  ].filter(Boolean).join(" ").trim();
};

const IncomeStatement = () => {
  const { t } = useTranslation();
  const cardRef = useRef(null);
  const [month, setMonth] = useState(dayjs());
  const [isSaving, setIsSaving] = useState(false);

  const user = useSelector((state) => state.dashboard?.appLayout?.user);
  const conversionFactor = useSelector((state) => state?.user?.conversionFactor);
  const userSelectedCurrency = useSelector((state) => state.user?.selectedCurrency);

  const startDate = month.startOf("month").format("YYYY-MM-DD");
  const endDate = month.endOf("month").format("YYYY-MM-DD");
  const earnings = ApiHook.CallMonthlyIncomeStatement(startDate, endDate, true);

  // current rank — resolved from the (cached) dashboard details payload
  const dashboard = ApiHook.CallDashboardDetails();
  const ranks = dashboard?.data?.ranks ?? [];
  const currentRankName =
    ranks.find((r) => r.id === dashboard?.data?.currentRank)?.name ?? "—";

  const incomeRows = () => {
    const buckets = {};
    Object.keys(ALLOWED_TYPES).forEach((key) => {
      buckets[key] = { gross: 0, tds: 0, admin: 0, net: 0 };
    });
    // RBV bonus is split into Personal / Team, aggregated from each row's `data` JSON
    const rbvSplitBuckets = RBV_SPLITS.map(() => ({ gross: 0, tds: 0, admin: 0, net: 0 }));

    (earnings?.data?.data ?? []).forEach((row) => {
      const type = SUB_TYPE_PARENT[row.amountType] ?? row.amountType;
      if (type === "rbv_bonus") {
        const md = parseMatchDetails(row.data);
        if (md) {
          RBV_SPLITS.forEach((split, i) => {
            rbvSplitBuckets[i].gross += Number(md[split.fields.gross]) || 0;
            rbvSplitBuckets[i].admin += Number(md[split.fields.admin]) || 0;
            rbvSplitBuckets[i].tds   += Number(md[split.fields.tds]) || 0;
            rbvSplitBuckets[i].net   += Number(md[split.fields.net]) || 0;
          });
        }
        return;
      }
      if (!buckets[type]) return;
      buckets[type].gross += Number(row.totalAmount) || 0;
      buckets[type].tds += Number(row.tds) || 0;
      buckets[type].admin += Number(row.serviceCharge) || 0;
      buckets[type].net += Number(row.amountPayable) || 0;
    });

    // Build ordered display rows, substituting the two RBV splits for the rbv_bonus row
    const displayRows = [];
    Object.keys(ALLOWED_TYPES).forEach((type) => {
      if (type === "rbv_bonus") {
        RBV_SPLITS.forEach((split, i) => {
          displayRows.push({
            key: split.key,
            label: split.label,
            icon: split.icon,
            ...rbvSplitBuckets[i],
          });
        });
      } else {
        displayRows.push({
          key: type,
          label: ALLOWED_TYPES[type],
          icon: INCOME_ICONS[type] ?? "fas fa-coins",
          ...buckets[type],
        });
      }
    });
    return displayRows;
  };

  const rows = incomeRows();
  const total = rows.reduce(
    (acc, row) => ({
      gross: acc.gross + row.gross,
      tds: acc.tds + row.tds,
      admin: acc.admin + row.admin,
      net: acc.net + row.net,
    }),
    { gross: 0, tds: 0, admin: 0, net: 0 }
  );

  const formatAmount = (value) =>
    Number(CurrencyConverter(value, conversionFactor)).toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

  const symbol = userSelectedCurrency?.symbolLeft ?? "₹";
  const totalConverted = Number(CurrencyConverter(total.gross, conversionFactor)) || 0;
  const totalWords = numberToIndianWords(totalConverted) + " Only";

  const handleSaveAsPNG = () => {
    if (cardRef.current) {
      setIsSaving(true);
      html2canvas(cardRef.current, {
        scale: 2,
        useCORS: true,
        backgroundColor: "#ffffff",
      })
        .then((canvas) => {
          const link = document.createElement("a");
          link.download = `income-statement-${user?.username ?? "user"}-${month.format("MMM-YYYY")}.png`;
          link.href = canvas.toDataURL("image/png");
          link.click();
        })
        .finally(() => setIsSaving(false));
    }
  };

  const twoLine = (text) =>
    String(text).split("\n").map((line, i, arr) => (
      <React.Fragment key={i}>
        {line}
        {i < arr.length - 1 && <br />}
      </React.Fragment>
    ));

  const renderField = (icon, label, value) => (
    <div className="ans_field">
      <span className="ans_field_icon">
        <i className={icon}></i>
      </span>
      <div className="ans_field_body">
        <span className="ans_field_label">{label}</span>
        <span className="ans_field_colon">:</span>
        <span className="ans_field_value">{value || "—"}</span>
      </div>
    </div>
  );

  return (
    <>
      <div className="page_head_top">{t("monthly_income_statement")}</div>

      <div className="ewallet_top_btn_sec">
        <div className="row justify-content-between align-items-center">
          <div className="col-auto d-flex align-items-center gap-2">
            <label style={{ fontWeight: 600, fontSize: 13, marginBottom: 0 }}>{t("month")}:</label>
            <DatePicker
              picker="month"
              allowClear={false}
              value={month}
              format="MMMM YYYY"
              disabledDate={(date) => date.isAfter(dayjs(), "month")}
              onChange={(value) => value && setMonth(value)}
            />
          </div>
          <div className="col-auto">
            <button
              className="btn_ewallt_page"
              style={{ background: "#136a6e", color: "#fff", border: "none", cursor: "pointer" }}
              onClick={handleSaveAsPNG}
              disabled={isSaving || earnings?.isLoading}
            >
              <i className="fa fa-download" style={{ marginRight: 6 }}></i>
              {isSaving ? t("loading") : "Save as PNG"}
            </button>
          </div>
        </div>
      </div>

      {earnings?.isLoading && (
        <div className="mis_loading" style={{ clear: "both" }}>{t("loading")}...</div>
      )}

      <div className="mis_statement_holder" style={{ clear: "both" }}>
        <div className="ans_card" ref={cardRef}>
          {/* ---------- decorative background ---------- */}
          {/* <svg className="ans_corner_swoosh" viewBox="0 0 230 230" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M230 0 C150 6 92 64 84 150 C140 150 196 104 218 44 C224 28 228 14 230 0Z" fill="#ee5a5e" />
            <path d="M230 22 C168 30 120 74 108 140 C104 162 108 180 124 188 C120 150 140 96 196 64 C214 54 226 40 230 22Z" fill="#f7f2ee" opacity="0.55" />
            <path d="M230 54 C182 62 146 96 134 146" stroke="#ffffff" strokeWidth="2.4" strokeLinecap="round" opacity="0.7" fill="none" />
          </svg> */}

          <svg className="ans_bg_leaf ans_bg_leaf_left" width="120" height="150" viewBox="0 0 120 150" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M14 6 C70 18 96 70 90 140" stroke="#cfe6e5" strokeWidth="2" fill="none" strokeLinecap="round" />
            <path d="M30 30 C50 24 70 34 78 54 C56 60 38 50 30 30Z" fill="#e7f3f2" />
            <path d="M44 64 C64 58 84 68 92 88 C70 94 52 84 44 64Z" fill="#e7f3f2" />
            <path d="M56 100 C76 94 96 104 104 124 C82 130 64 120 56 100Z" fill="#e7f3f2" />
          </svg>
          <svg className="ans_bg_leaf ans_bg_leaf_right" width="120" height="150" viewBox="0 0 120 150" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M106 6 C50 18 24 70 30 140" stroke="#cfe6e5" strokeWidth="2" fill="none" strokeLinecap="round" />
            <path d="M90 30 C70 24 50 34 42 54 C64 60 82 50 90 30Z" fill="#e7f3f2" />
            <path d="M76 64 C56 58 36 68 28 88 C50 94 68 84 76 64Z" fill="#e7f3f2" />
            <path d="M64 100 C44 94 24 104 16 124 C38 130 56 120 64 100Z" fill="#e7f3f2" />
          </svg>

          <div className="ans_inner">
            {/* ---------- header ---------- */}
            <div className="ans_head">
              <img className="ans_logo" src="/images/logo_user.png" alt="Ayush Natura Wellness" />
              <h1 className="ans_title">{t("monthly_income_statement", "Monthly Income Statement")}</h1>
              <div className="ans_title_divider">
                <svg width="120" height="22" viewBox="0 0 120 22" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <line x1="2" y1="11" x2="44" y2="11" stroke="#cfe6e5" strokeWidth="2" strokeLinecap="round" />
                  <line x1="76" y1="11" x2="118" y2="11" stroke="#cfe6e5" strokeWidth="2" strokeLinecap="round" />
                  <path d="M60 3 C55 9 55 13 60 19 C65 13 65 9 60 3Z" fill="#ee5a5e" />
                  <path d="M52 8 C54 13 57 16 60 17 C58 12 56 9 52 8Z" fill="#136a6e" />
                  <path d="M68 8 C66 13 63 16 60 17 C62 12 64 9 68 8Z" fill="#136a6e" />
                </svg>
              </div>
            </div>

            {/* ---------- associate details ---------- */}
            <div className="ans_section">
              <div className="ans_tab">
                <span className="ans_tab_icon"><i className="fas fa-user"></i></span>
                {t("associate_details", "Associate Details")}
              </div>
              <div className="ans_assoc_box">
                <div className="ans_assoc_col">
                  {renderField("fas fa-id-card", t("user_id", "User ID"), user?.username)}
                  {renderField("fas fa-user", t("associate_name", "Associate Name"), user?.name)}
                </div>
                <div className="ans_assoc_div"></div>
                <div className="ans_assoc_col">
                  {renderField("fas fa-crown", t("current_rank", "Current Rank"), currentRankName)}
                  {renderField("fas fa-calendar-alt", t("statement_month", "Statement Month"), month.format("MMMM YYYY"))}
                </div>
              </div>
            </div>

            {/* ---------- income summary banner ---------- */}
            <div className="ans_banner">
              <span className="ans_dots ans_dots_left"><span></span><span></span><span></span></span>
              <span className="ans_banner_pill">{t("income_summary", "Income Summary")}</span>
              <span className="ans_dots"><span></span><span></span><span></span></span>
            </div>

            {/* ---------- table ---------- */}
            <table className="ans_table">
              <thead>
                <tr>
                  <th>{t("income_head", "Income Head")}</th>
                  <th>{t("amount", "Amount")} <small>({symbol})</small></th>
                  <th>{t("admin_charges", "Admin Charges")} <small>({symbol})</small></th>
                  <th>{t("tds_if_applicable", "TDS (If Applicable)")} <small>({symbol})</small></th>
                  <th>{t("net_amount", "Net Amount")} <small>({symbol})</small></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.key}>
                    <td>
                      <span className="ans_head_cell">
                        <span className="ans_head_chip">
                          <i className={row.icon ?? "fas fa-coins"}></i>
                        </span>
                        {row.label}
                      </span>
                    </td>
                    <td>{formatAmount(row.gross)}</td>
                    <td>{formatAmount(row.admin)}</td>
                    <td>{formatAmount(row.tds)}</td>
                    <td>{formatAmount(row.net)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td>
                    <span className="ans_total_cell">
                      <i className="fas fa-coins"></i>
                      {t("total_income", "Total Income")}
                    </span>
                  </td>
                  <td>{formatAmount(total.gross)}</td>
                  <td>{formatAmount(total.admin)}</td>
                  <td>{formatAmount(total.tds)}</td>
                  <td>{formatAmount(total.net)}</td>
                </tr>
              </tfoot>
            </table>

            {/* ---------- total amount ---------- */}
            <div className="ans_total_box">
              <div className="ans_total_left">
                <span className="ans_total_bag"><i className="fas fa-sack-dollar"></i></span>
                <b>{t("total_amount", "Total Amount")}</b>
              </div>
              <div className="ans_total_right">
                <div className="ans_total_amt">
                  <span>{symbol}</span>{formatAmount(total.gross)}
                </div>
                <div className="ans_total_words">({totalWords})</div>
              </div>
            </div>

            {/* ---------- rank achieved ---------- */}
            <div className="ans_rank_row">
              <div className="ans_ach_circle">
                <svg viewBox="0 0 116 116" width="116" height="116" xmlns="http://www.w3.org/2000/svg">
                  <defs>
                    <linearGradient id="ansSky" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0" stopColor="#e9f5f4" />
                      <stop offset="1" stopColor="#cfe9e7" />
                    </linearGradient>
                  </defs>
                  <rect width="116" height="116" fill="url(#ansSky)" />
                  <circle cx="86" cy="32" r="13" fill="#fbe3e3" />
                  <path d="M0 116 L30 64 L58 116Z" fill="#1d7d80" />
                  <path d="M34 116 L72 52 L116 116Z" fill="#136a6e" />
                  <path d="M70 116 L98 70 L116 100 L116 116Z" fill="#0f4c4e" />
                  <g fill="#0f4c4e">
                    <circle cx="52" cy="78" r="5" />
                    <rect x="48" y="82" width="8" height="16" rx="3" />
                    <circle cx="66" cy="80" r="4.5" />
                    <rect x="62" y="84" width="7.5" height="14" rx="3" />
                  </g>
                  <line x1="44" y1="64" x2="44" y2="98" stroke="#0f4c4e" strokeWidth="2.4" strokeLinecap="round" />
                  <path d="M44 64 L62 70 L44 76Z" fill="#ee5a5e" />
                </svg>
              </div>
              <div className="ans_rank_box">
                <span className="ans_rank_medal"><i className="fas fa-award"></i></span>
                <div className="ans_rank_text">
                  <div className="ans_rank_label">{t("rank_achieved_during_the_month", "Rank Achieved During the Month")}</div>
                  <div className="ans_rank_value">{currentRankName}</div>
                </div>
                <span className="ans_rank_chart"><i className="fas fa-chart-line"></i></span>
              </div>
            </div>

            {/* ---------- tagline ---------- */}
            <div className="ans_tagline">
              <span className="t1">{t("together_we_grow", "Together We Grow,")}</span>
              <br />
              <span className="t2">{t("together_we_succeed", "Together We Succeed!")}</span>
            </div>

            {/* ---------- features ---------- */}
            <div className="ans_features">
              <div className="ans_feature">
                <span className="ans_feature_icon"><i className="fas fa-seedling"></i></span>
                <span className="ans_feature_text">{twoLine(t("wellness_for_life", "Wellness\nfor Life"))}</span>
              </div>
              <div className="ans_feature">
                <span className="ans_feature_icon"><i className="fas fa-users"></i></span>
                <span className="ans_feature_text">{twoLine(t("opportunity_for_all", "Opportunity\nfor All"))}</span>
              </div>
              <div className="ans_feature">
                <span className="ans_feature_icon"><i className="fas fa-bullseye"></i></span>
                <span className="ans_feature_text">{twoLine(t("success_for_generations", "Success for\nGenerations"))}</span>
              </div>
            </div>
          </div>

          {/* ---------- footer bar ---------- */}
          <div className="ans_footer_bar">
            <span className="ans_footer_badge"><i className="fas fa-leaf"></i></span>
            <p>{t("income_statement_footer_note", "This statement reflects the incentives and rewards earned during the specified month as per the Ayush Natura Wellness Marketing Plan.")}</p>
          </div>
        </div>
      </div>
    </>
  );
};

export default IncomeStatement;
